import React, { useState } from 'react';
import type { Clinician } from '@features/organizations/types';
import { ProviderBadge } from '@jotpsych/ui';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

interface OrgScheduleProviderFilterProps {
  clinicians: Clinician[];
  selectedProviderIds: string[];
  onSelectionChange: (ids: string[]) => void;
  isLoading: boolean;
}

export const OrgScheduleProviderFilter: React.FC<OrgScheduleProviderFilterProps> = ({
  clinicians,
  selectedProviderIds,
  onSelectionChange,
  isLoading,
}) => {
  const [expanded, setExpanded] = useState(true);
  const { orgSchedule } = useEventTracking();

  const handleToggle = (userId: string) => {
    const next = selectedProviderIds.includes(userId)
      ? selectedProviderIds.filter((id) => id !== userId)
      : [...selectedProviderIds, userId];
    updateProviderSelection(next, 'individual_toggle');
  };

  const handleOnly = (event: React.MouseEvent, userId: string) => {
    event.stopPropagation();
    updateProviderSelection([userId], 'only');
  };

  const handleShowAll = () => {
    updateProviderSelection(clinicians.map(clinician => clinician.userId), 'show_all');
  };

  const updateProviderSelection = (
    next: string[],
    action: 'individual_toggle' | 'only' | 'show_all',
  ) => {
    onSelectionChange(next);
    orgSchedule.providerFilterChanged({
      selected_count: next.length,
      total_count: clinicians.length,
      action,
    });
  };

  const allProvidersSelected =
    clinicians.length > 0 && selectedProviderIds.length === clinicians.length;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 400, color: '#535976' }}>
            Providers
          </Typography>
          {!isLoading && clinicians.length > 0 && (
            <Typography sx={{ fontSize: '12px', color: '#8F95AA' }}>
              {selectedProviderIds.length}/{clinicians.length}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!isLoading && clinicians.length > 0 && (
            <Button
              size="small"
              variant="text"
              disabled={allProvidersSelected}
              onClick={handleShowAll}
              sx={{
                minWidth: 0,
                px: 0.75,
                py: 0.25,
                color: '#535976',
                fontSize: 12,
                fontWeight: 650,
                textTransform: 'none',
                '&.Mui-disabled': { color: '#B7BCCB' },
              }}
            >
              Show all
            </Button>
          )}
          <IconButton
            size="small"
            onClick={() => setExpanded((prev) => !prev)}
            aria-label={expanded ? 'Collapse providers' : 'Expand providers'}
          >
            {expanded ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
            {clinicians.map((clinician) => (
              <ProviderFilterRow
                key={clinician.userId}
                clinician={clinician}
                isSelected={selectedProviderIds.includes(clinician.userId)}
                isOnlySelected={
                  selectedProviderIds.length === 1 &&
                  selectedProviderIds.includes(clinician.userId)
                }
                onToggle={handleToggle}
                onOnly={handleOnly}
              />
            ))}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

interface ProviderFilterRowProps {
  clinician: Clinician;
  isSelected: boolean;
  isOnlySelected: boolean;
  onToggle: (userId: string) => void;
  onOnly: (event: React.MouseEvent, userId: string) => void;
}

const ProviderFilterRow: React.FC<ProviderFilterRowProps> = ({
  clinician,
  isSelected,
  isOnlySelected,
  onToggle,
  onOnly,
}) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={() => onToggle(clinician.userId)}
    onKeyDown={(event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      onToggle(clinician.userId);
    }}
    aria-pressed={isSelected}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      width: '100%',
      minWidth: 0,
      minHeight: 32,
      px: 0.75,
      py: 0.5,
      border: '1px solid transparent',
      borderRadius: '6px',
      bgcolor: isSelected ? '#F3F5FA' : 'transparent',
      color: 'inherit',
      cursor: 'pointer',
      textAlign: 'left',
      '&:hover': {
        bgcolor: isSelected ? '#EEF1F7' : '#F8F9FC',
        '.ProviderFilterRow-only': {
          opacity: 1,
        },
      },
      '&:focus-within .ProviderFilterRow-only': {
        opacity: 1,
      },
      '&:focus-visible': {
        outline: '2px solid #2563EB',
        outlineOffset: 1,
      },
    }}
  >
    <ProviderBadge name={clinician.name} selected={isSelected} />
    <Typography
      sx={{
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: isSelected ? '#2F344F' : '#6F758A',
        fontSize: 14,
        fontWeight: isSelected ? 650 : 500,
      }}
    >
      {clinician.name}
    </Typography>
    <Button
      className="ProviderFilterRow-only"
      size="small"
      variant="text"
      disabled={isOnlySelected}
      onClick={(event) => onOnly(event, clinician.userId)}
      sx={{
        minWidth: 0,
        px: 0.5,
        py: 0,
        color: '#535976',
        fontSize: 12,
        fontWeight: 650,
        lineHeight: 1.4,
        opacity: isOnlySelected ? 1 : 0,
        textTransform: 'none',
        transition: 'opacity 120ms ease-out',
        '&.Mui-disabled': {
          color: '#A5ABBC',
        },
      }}
    >
      Only
    </Button>
  </Box>
);
